import { useCallback, useEffect, useRef } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { useBusinessId } from "@/hooks/useBusinessId";
import { notificationsInboxApi } from "@/api/notifications";
import { useSseSubscription } from "@/hooks/useSseSubscription";

const NOTIFICATIONS_BASE_URL =
  (import.meta.env.VITE_NOTIFICATIONS_URL as string | undefined) || "http://localhost:8084";

const FALLBACK_REFETCH_MS = 5 * 60 * 1000;

/**
 * Only the newest handful matters here — SSE refetches on every arrival, so anything
 * older than this page has already been seen and toasted. This hook mounts app-wide,
 * so fetching the unpaged inbox made every screen pay for the whole notification history.
 */
const TOAST_LOOKBACK_SIZE = 20;

/**
 * Subscribes to the notifications SSE stream and shows a toast for any new
 * unread notifications. Compares latest notification ID against a ref to
 * avoid duplicates.
 */
export function useNotificationToast() {
  const businessId = useBusinessId();
  const queryClient = useQueryClient();
  const lastSeenIdRef = useRef<number>(0);
  const initializedRef = useRef(false);

  const { data: notificationPage } = useQuery({
    queryKey: ["notifications", businessId, "toast-lookback"],
    queryFn: () => notificationsInboxApi.listPaged(businessId, 0, TOAST_LOOKBACK_SIZE),
    enabled: !!businessId,
    refetchInterval: FALLBACK_REFETCH_MS,
  });

  const notifications = notificationPage?.content;

  const refreshNotifications = useCallback(() => {
    if (!businessId) return;
    void queryClient.invalidateQueries({ queryKey: ["notifications", businessId] });
    void queryClient.invalidateQueries({ queryKey: ["notifications-unread", businessId] });
  }, [queryClient, businessId]);

  useSseSubscription({
    url: businessId
      ? `${NOTIFICATIONS_BASE_URL}/api/v1/businesses/${businessId}/notifications/stream`
      : null,
    onEvent: refreshNotifications,
    onOpen: refreshNotifications,
    deps: [businessId],
  });

  useEffect(() => {
    if (!notifications || notifications.length === 0) return;

    const latestId = Math.max(...notifications.map((n) => n.id));
    if (!initializedRef.current) {
      lastSeenIdRef.current = latestId;
      initializedRef.current = true;
      return;
    }

    // Skip events the staff member just triggered — they already got
    // inline feedback (e.g. "Cita creada exitosamente" toast).
    const SELF_ACTION_EVENTS = new Set([
      "APPOINTMENT_CREATED",
      "APPOINTMENT_CONFIRMED",
      "APPOINTMENT_COMPLETED",
      "APPOINTMENT_CANCELLED",
      "APPOINTMENT_NO_SHOW",
    ]);

    const newNotifications = notifications
      .filter((n) => n.id > lastSeenIdRef.current && !n.read && !SELF_ACTION_EVENTS.has(n.eventType))
      .sort((a, b) => a.id - b.id);

    for (const n of newNotifications) {
      toast(n.title, {
        description: n.body,
        duration: 5000,
      });
    }

    if (latestId > lastSeenIdRef.current) {
      lastSeenIdRef.current = latestId;
    }
  }, [notifications]);
}
